export function sidebarTocFunc() {
  const content =
    document.querySelector('.p-single-body__body') ||
    document.querySelector('.entry-content');

  if (!content) {
    return;
  }

  const headings = getContentHeadings(content);

  setupSidebarToc(headings);
  setupSingleBodyToc(headings);
}

function getContentHeadings(content) {
  return Array.from(content.querySelectorAll('h2, h3')).filter((heading) => {
    return heading.textContent.trim() !== '';
  });
}

function setupSidebarToc(headings) {
  const toc = document.querySelector('[data-sidebar-toc]');

  if (!toc) {
    return;
  }

  const tocItem = toc.closest('.c-sidebar__item--toc');

  if (headings.length === 0) {
    if (tocItem) {
      tocItem.style.display = 'none';
    }
    return;
  }

  const entries = buildToc(toc, headings);
  bindActiveState(entries);
  initSidebarTocCustomScrollbar(toc);
}

/**
 * スクロールが発生する要素だけに目次リストを載せ、レールは外に置く（レールを子にすると scrollHeight がズレることがある）。
 * @param {HTMLElement} scrollEl
 * @returns {HTMLElement}
 */
function ensureTocScrollbarShell(scrollEl) {
  const parent = scrollEl.parentElement;
  if (!parent) {
    return scrollEl;
  }

  if (parent.classList.contains('c-sidebar__toc-shell')) {
    return parent;
  }

  const shell = document.createElement('div');
  shell.className = 'c-sidebar__toc-shell';
  parent.insertBefore(shell, scrollEl);
  shell.appendChild(scrollEl);
  return shell;
}

/**
 * サイドバー目次: `.c-sidebar__toc-shell` で包んでレールを scroll の兄弟にする（キャッシュされていないとき JS が追加）。
 */
function initSidebarTocCustomScrollbar(tocRoot) {
  const scrollEl = tocRoot.closest('.c-sidebar__toc-scroll');
  if (!scrollEl || scrollEl.dataset.tocScrollbarBound === '1') {
    return;
  }

  scrollEl.dataset.tocScrollbarBound = '1';

  const shell = ensureTocScrollbarShell(scrollEl);
  Array.from(scrollEl.children).forEach((node) => {
    if (node.classList.contains('c-sidebar__toc-scrollbar')) {
      node.remove();
    }
  });

  const mqDesktop = window.matchMedia('(min-width: 1280px)');

  const rail = document.createElement('div');
  rail.className = 'c-sidebar__toc-scrollbar';
  rail.setAttribute('aria-hidden', 'true');

  const track = document.createElement('div');
  track.className = 'c-sidebar__toc-scrollbar__track';

  const thumb = document.createElement('div');
  thumb.className = 'c-sidebar__toc-scrollbar__thumb';

  track.appendChild(thumb);
  rail.appendChild(track);
  shell.appendChild(rail);

  let isDraggingThumb = false;
  /** @type {number | null} */
  let dragPointerId = null;

  /** スクロール毎には動かさない（細かく揺れないようにする）。位置は syncThumbOnly で最新寸法から再計算する */
  /** @type {number | null} */
  let cachedThumbH = null;

  const getMetrics = () => {
    const { scrollHeight, clientHeight } = scrollEl;
    const trackH = track.clientHeight;
    const thumbMin = 28;
    const ratio = scrollHeight > 0 ? clientHeight / scrollHeight : 1;
    const thumbH = Math.min(
      trackH,
      Math.max(thumbMin, ratio * trackH),
    );
    const maxScroll = Math.max(0, scrollHeight - clientHeight);
    const maxTop = Math.max(0, trackH - thumbH);
    return { trackH, thumbH, maxScroll, maxTop };
  };

  /**
   * @param {number} scrollTop
   * @param {number} maxScroll
   * @param {number} maxTop
   */
  const clampThumbTop = (scrollTop, maxScroll, maxTop) => {
    if (maxTop <= 0 || maxScroll <= 0) {
      return 0;
    }

    /** 下端でブラウザの scrollTop 丸めで比率がずれないよう吸着する */
    if (scrollTop >= maxScroll - 1.5) {
      return maxTop;
    }
    if (scrollTop <= 1.5) {
      return 0;
    }

    return Math.min(maxTop, Math.max(0, (scrollTop / maxScroll) * maxTop));
  };

  /** サム「高さ」は cachedThumbH で固定し、スクロール中に変わる scrollHeight に追従するのは比率だけにする */
  const thumbGeometryFromCached = () => {
    const { scrollHeight, clientHeight } = scrollEl;
    const trackH = track.clientHeight;
    const maxScroll = Math.max(0, scrollHeight - clientHeight);
    if (
      cachedThumbH == null ||
      trackH < 4 ||
      scrollHeight <= clientHeight + 2
    ) {
      return null;
    }

    const thumbH = Math.min(cachedThumbH, trackH);
    const maxTop = Math.max(0, trackH - thumbH);
    return { thumbH, maxScroll, maxTop, trackH };
  };

  /** @type {{ clientY: number; thumbTop: number }} */
  let thumbDragStart = { clientY: 0, thumbTop: 0 };

  const onThumbPointerMove = (ev) => {
    if (!isDraggingThumb || ev.pointerId !== dragPointerId) {
      return;
    }

    const g = thumbGeometryFromCached();
    if (!g || g.maxTop <= 0 || g.maxScroll <= 0) {
      return;
    }

    const { maxScroll, maxTop } = g;

    const dy = ev.clientY - thumbDragStart.clientY;
    let newTop = thumbDragStart.thumbTop + dy;
    newTop = Math.max(0, Math.min(maxTop, newTop));
    thumb.style.top = `${newTop}px`;
    scrollEl.scrollTop = (newTop / maxTop) * maxScroll;
  };

  const endThumbDrag = (ev) => {
    if (dragPointerId == null || ev.pointerId !== dragPointerId) {
      return;
    }

    try {
      thumb.releasePointerCapture(ev.pointerId);
    } catch {
      // already released
    }

    dragPointerId = null;
    isDraggingThumb = false;
    shell.classList.remove('is-toc-scrollbar-dragging');

    thumb.removeEventListener('pointermove', onThumbPointerMove);
    thumb.removeEventListener('pointerup', endThumbDrag);
    thumb.removeEventListener('pointercancel', endThumbDrag);
    thumb.removeEventListener('pointerlost', endThumbDrag);

    measureLayout();
  };

  const abortThumbDragIfNeeded = () => {
    if (!isDraggingThumb) {
      return;
    }
    if (dragPointerId != null) {
      try {
        thumb.releasePointerCapture(dragPointerId);
      } catch {
        // ignore
      }
    }
    dragPointerId = null;
    isDraggingThumb = false;
    shell.classList.remove('is-toc-scrollbar-dragging');
    thumb.removeEventListener('pointermove', onThumbPointerMove);
    thumb.removeEventListener('pointerup', endThumbDrag);
    thumb.removeEventListener('pointercancel', endThumbDrag);
    thumb.removeEventListener('pointerlost', endThumbDrag);
  };

  const syncThumbOnly = () => {
    if (cachedThumbH == null || isDraggingThumb) {
      return;
    }
    if (!scrollEl.classList.contains('is-toc-scrollable')) {
      return;
    }

    const g = thumbGeometryFromCached();
    if (!g) {
      return;
    }

    const { thumbH, maxScroll, maxTop } = g;
    const { scrollTop } = scrollEl;
    thumb.style.height = `${thumbH}px`;
    thumb.style.top = `${clampThumbTop(scrollTop, maxScroll, maxTop)}px`;
  };

  const measureLayout = () => {
    if (!mqDesktop.matches) {
      abortThumbDragIfNeeded();
      cachedThumbH = null;
      scrollEl.classList.remove('is-toc-scrollable');
      shell.classList.remove('has-toc-scrollbar', 'is-toc-scrollbar-dragging');
      rail.style.display = 'none';
      return;
    }

    rail.style.display = '';

    const { scrollHeight, clientHeight, scrollTop } = scrollEl;
    const trackH = track.clientHeight;

    if (trackH < 4) {
      return;
    }

    if (scrollHeight <= clientHeight + 2) {
      abortThumbDragIfNeeded();
      cachedThumbH = null;
      scrollEl.classList.remove('is-toc-scrollable');
      shell.classList.remove('has-toc-scrollbar', 'is-toc-scrollbar-dragging');
      rail.style.visibility = 'hidden';
      return;
    }

    scrollEl.classList.add('is-toc-scrollable');
    shell.classList.add('has-toc-scrollbar');
    rail.style.visibility = 'visible';

    /* ドラッグ中は pointermove と scroll が競合しないよう、レイアウト確定のみスキップ */
    if (isDraggingThumb) {
      return;
    }

    const { thumbH, maxScroll, maxTop } = getMetrics();
    cachedThumbH = thumbH;
    thumb.style.height = `${thumbH}px`;
    thumb.style.top = `${clampThumbTop(scrollTop, maxScroll, maxTop)}px`;
  };

  thumb.addEventListener('pointerdown', (ev) => {
    if (ev.pointerType === 'mouse' && ev.button !== 0) {
      return;
    }
    if (!scrollEl.classList.contains('is-toc-scrollable')) {
      return;
    }

    ev.preventDefault();
    ev.stopPropagation();

    const { maxScroll, maxTop } = getMetrics();
    if (maxTop <= 0 || maxScroll <= 0) {
      return;
    }

    const thumbTopPx = parseFloat(thumb.style.top);
    thumbDragStart = {
      clientY: ev.clientY,
      thumbTop: Number.isFinite(thumbTopPx) ? thumbTopPx : 0,
    };

    isDraggingThumb = true;
    dragPointerId = ev.pointerId;
    shell.classList.add('is-toc-scrollbar-dragging');

    thumb.setPointerCapture(ev.pointerId);
    thumb.addEventListener('pointermove', onThumbPointerMove);
    thumb.addEventListener('pointerup', endThumbDrag);
    thumb.addEventListener('pointercancel', endThumbDrag);
    thumb.addEventListener('pointerlost', endThumbDrag);
  });

  track.addEventListener('pointerdown', (ev) => {
    if (ev.pointerType === 'mouse' && ev.button !== 0) {
      return;
    }
    if (ev.target !== track) {
      return;
    }
    if (!scrollEl.classList.contains('is-toc-scrollable')) {
      return;
    }

    ev.preventDefault();

    const { thumbH, maxScroll, maxTop } = getMetrics();
    if (maxTop <= 0 || maxScroll <= 0) {
      return;
    }

    const rect = track.getBoundingClientRect();
    const yInTrack = ev.clientY - rect.top;
    let newTop = yInTrack - thumbH / 2;
    newTop = Math.max(0, Math.min(maxTop, newTop));
    thumb.style.top = `${newTop}px`;
    scrollEl.scrollTop = (newTop / maxTop) * maxScroll;
    measureLayout();
  });

  scrollEl.addEventListener('scroll', syncThumbOnly, { passive: true });
  window.addEventListener('resize', measureLayout);
  if (typeof mqDesktop.addEventListener === 'function') {
    mqDesktop.addEventListener('change', measureLayout);
  } else {
    mqDesktop.addListener(measureLayout);
  }

  const ro = new ResizeObserver(measureLayout);
  ro.observe(scrollEl);
  ro.observe(shell);
  ro.observe(tocRoot);

  requestAnimationFrame(measureLayout);
}

function setupSingleBodyToc(headings) {
  const toc = document.querySelector('[data-single-body-toc]');

  if (!toc) {
    return;
  }

  const tocWrap = toc.closest('[data-single-body-toc-wrap]');

  if (headings.length === 0) {
    if (tocWrap) {
      tocWrap.style.display = 'none';
    }
    return;
  }

  buildToc(toc, headings);
  bindSingleBodyTocToggle(tocWrap, toc);
}

function buildToc(toc, headings) {
  const entries = [];
  let currentParentItem = null;

  toc.innerHTML = '';

  headings.forEach((heading, index) => {
    const id = ensureHeadingId(heading, index);
    const level = heading.tagName.toLowerCase();
    const item = document.createElement('li');
    const link = document.createElement('a');

    link.setAttribute('href', `#${id}`);
    link.textContent = heading.textContent.trim();
    item.appendChild(link);
    entries.push({ heading, link });

    if (level === 'h2') {
      toc.appendChild(item);
      currentParentItem = item;
      return;
    }

    if (currentParentItem) {
      let childList = currentParentItem.querySelector('ul');

      if (!childList) {
        childList = document.createElement('ul');
        currentParentItem.appendChild(childList);
      }

      childList.appendChild(item);
      return;
    }

    toc.appendChild(item);
  });

  return entries;
}

function bindSingleBodyTocToggle(tocWrap, toc) {
  if (!tocWrap) {
    return;
  }

  const toggle = tocWrap.querySelector('[data-single-body-toc-toggle]');
  const toggleText = tocWrap.querySelector('[data-single-body-toc-toggle-text]');
  const panel = tocWrap.querySelector('[data-single-body-toc-panel]');

  if (!toggle) {
    return;
  }

  const setOpen = (isOpen) => {
    tocWrap.classList.toggle('is-closed', !isOpen);
    toc.removeAttribute('hidden');
    toc.style.display = '';
    toggle.setAttribute('aria-expanded', String(isOpen));

    if (panel) {
      if (isOpen) {
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('inert', '');
      }
    } else {
      toc.hidden = !isOpen;
      toc.style.display = isOpen ? '' : 'none';
    }

    if (toggleText) {
      toggleText.textContent = isOpen ? '閉じる' : '開く';
    }
  };

  const toggleOpen = () => {
    setOpen(tocWrap.classList.contains('is-closed'));
  };

  toggle.addEventListener('click', toggleOpen);
  toggle.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') {
      return;
    }

    event.preventDefault();
    toggleOpen();
  });

  setOpen(true);
}

function ensureHeadingId(heading, index) {
  if (heading.id) {
    return heading.id;
  }

  const baseId = `toc-heading-${index + 1}`;
  let id = baseId;
  let suffix = 2;

  while (document.getElementById(id)) {
    id = `${baseId}-${suffix}`;
    suffix += 1;
  }

  heading.id = id;
  return id;
}

function getHeaderOffset() {
  const header = document.querySelector('.l-header');
  return (header ? header.offsetHeight : 0) + 24;
}

function bindActiveState(entries) {
  let ticking = false;

  const setActive = () => {
    const offset = getHeaderOffset();
    const currentLine = window.scrollY + offset;
    let activeEntry = null;

    entries.forEach((entry) => {
      const top = entry.heading.getBoundingClientRect().top + window.scrollY;

      if (top <= currentLine) {
        activeEntry = entry;
      }
    });

    if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) {
      activeEntry = entries[entries.length - 1];
    }

    entries.forEach((entry) => {
      entry.link.classList.toggle('is-active', entry === activeEntry);
    });

    ticking = false;
  };

  const requestActiveUpdate = () => {
    if (ticking) {
      return;
    }

    ticking = true;
    window.requestAnimationFrame(setActive);
  };

  window.addEventListener('scroll', requestActiveUpdate, { passive: true });
  window.addEventListener('resize', requestActiveUpdate);
  setActive();
}
